"use client";

import { useState, useEffect, useRef } from "react";

// =============================================================================
// STAR LOADER
// =============================================================================
// Phase 0: White screen contracts to center → forms star → bloops out
// Reveals the dark site behind as it shrinks
// Pure CSS/DOM, no Three.js

interface StarLoaderProps {
  /** Called when loader animation completes and should transition to VOID */
  onComplete: () => void;
  /** External signal that assets are ready (triggers bloop) */
  assetsReady?: boolean;
  /**
   * Skip the post-bloop tail: no 5s CRT hold, no 1s fading dwell.
   * Use when there's no starfield emergence to coordinate with (e.g. /exp pages).
   */
  skipTail?: boolean;
}

type LoaderState = "entering" | "holding" | "snapping" | "blooping" | "fading" | "done";

// =============================================================================
// SPRING EASING
// =============================================================================
// Fast initial contraction, then oscillates as if fighting gravity

// Phase 1: Fast contraction (0-0.4 of time → 0-0.85 of progress)
// Phase 2: Spring wobble around target (0.4-1.0 of time → 0.85-1.0 of progress)
const springEase = (t: number): number => {
  if (t < 0.4) {
    // Fast cubic ease-out for initial snap
    const p = t / 0.4;
    return (1 - Math.pow(1 - p, 3)) * 0.85;
  } else {
    // Spring oscillation for the remaining progress
    const p = (t - 0.4) / 0.6; // 0 to 1 over remaining time
    const base = 0.85 + p * 0.15; // 0.85 to 1.0

    // Decaying oscillation - fighting to reach target
    const frequency = 4;
    const decay = 3;
    const oscillation = Math.sin(p * Math.PI * frequency) * Math.exp(-p * decay) * 0.08;

    return base + oscillation;
  }
};

// =============================================================================
// CRT CORNER TEXT
// =============================================================================
// Glitch character map (subset from useLetterGlitch)
const GLITCH_MAP: Record<string, string> = {
  V: "∀", N: "И", G: "6", U: "∩", I: "|", E: "3", A: "4", O: "0", S: "5", T: "7",
  v: "∀", n: "и", g: "6", u: "∩", i: "|", e: "3", a: "4", o: "0", s: "5", t: "7",
};

// Number swap glitch - l33t speak flavor
const NUMBER_MAP: Record<string, string> = {
  I: "1", L: "1", J: "1",
  i: "1", l: "1", j: "1",
  Z: "2", Q: "2",
  z: "2", q: "2",
  E: "3",
  e: "3",
  A: "4", F: "4",
  a: "4", f: "4",
  S: "5",
  s: "5",
  G: "6", B: "6",
  g: "6", b: "6",
  T: "7",
  t: "7",
  O: "0",
  o: "0",
};

// Boot text - version is injected at runtime
const getBootText = (version?: string) => {
  const port = typeof window !== "undefined" && window.location.port ? `:${window.location.port}` : "";
  const worktreeRaw = process.env.NEXT_PUBLIC_WORKTREE;
  const branch = process.env.NEXT_PUBLIC_GIT_BRANCH;
  const branchSuffix = branch && branch !== "main" && branch !== "HEAD" ? `@${branch}` : "";
  // Skip worktree suffix when branch already identifies it (they share the same name).
  const worktree = worktreeRaw && worktreeRaw !== "main" && !branchSuffix ? `-${worktreeRaw}` : "";
  const versionPart = version ? `V${version.toUpperCase()}` : "";
  const versionStr = versionPart || branchSuffix || worktree || port
    ? `INITIALIZING ${versionPart}${branchSuffix}${worktree}${port}`.trim()
    : "INITIALIZING";
  return [
    { pos: "top-left", text: "LOADING" },
    { pos: "top-right", text: "STAND BY" },
    { pos: "bottom-center", text: versionStr },
  ] as const;
};

// =============================================================================
// CRT BOOT TEXT (exported for use in page.tsx)
// =============================================================================
// Stays visible through loader and void phases, fades when ring forms

interface CRTBootTextProps {
  visible: boolean;
  fadeOut: boolean;
  crtEnabled?: boolean;
  version?: string;
}

export function CRTBootText({ visible, fadeOut, crtEnabled = true, version }: CRTBootTextProps) {
  const bootText = getBootText(version);
  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none">
      {bootText.map((item, index) => (
        <GlitchText
          key={item.pos}
          pos={item.pos}
          text={item.text}
          visible={visible}
          fadeOut={fadeOut}
          index={index}
          crtEnabled={crtEnabled}
        />
      ))}
    </div>
  );
}

// Glitch text with CRT effect
function GlitchText({
  pos,
  text,
  visible,
  fadeOut,
  index,
  crtEnabled = true
}: {
  pos: "top-left" | "top-center" | "top-right" | "bottom-center";
  text: string;
  visible: boolean;
  fadeOut: boolean;
  index: number;
  crtEnabled?: boolean;
}) {
  const [displayText, setDisplayText] = useState(text);
  const [isGlitching, setIsGlitching] = useState(true); // Start with glitch
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Glitch effect - on mount and every 2s
  // Two flavors: symbol swap, then number swap
  useEffect(() => {
    if (!visible) return;

    const applyGlitch = (glitchMap: Record<string, string>) => {
      const chars = text.split("");
      const glitchableIndices: number[] = [];

      chars.forEach((char, i) => {
        if (glitchMap[char]) glitchableIndices.push(i);
      });

      if (glitchableIndices.length === 0) return text;

      // Pick 1-2 random chars to glitch
      const numGlitches = Math.min(2, Math.floor(Math.random() * 2) + 1, glitchableIndices.length);
      const shuffled = [...glitchableIndices].sort(() => Math.random() - 0.5);
      const toGlitch = shuffled.slice(0, numGlitches);

      return chars.map((char, i) =>
        toGlitch.includes(i) && glitchMap[char] ? glitchMap[char] : char
      ).join("");
    };

    const glitchSequence = () => {
      setIsGlitching(true);

      // Phase 1: Symbol glitch
      const symbolGlitched = applyGlitch(GLITCH_MAP);
      setDisplayText(symbolGlitched);

      // Phase 2: Number glitch (after 80ms)
      setTimeout(() => {
        const numberGlitched = applyGlitch(NUMBER_MAP);
        setDisplayText(numberGlitched);

        // Reset after another 80ms
        setTimeout(() => {
          setDisplayText(text);
          setIsGlitching(false);
        }, 80);
      }, 80);
    };

    // Initial glitch on mount (staggered)
    const initialDelay = index * 100;
    setTimeout(glitchSequence, initialDelay);

    // Glitch every 1.2s
    intervalRef.current = setInterval(glitchSequence, 1200);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [visible, text, index]);

  const positionClasses = {
    "top-left": "top-4 left-4",
    "top-center": "top-4 left-1/2 -translate-x-1/2 text-center",
    "top-right": "top-4 right-4 text-right",
    "bottom-center": "bottom-4 left-1/2 -translate-x-1/2 text-center",
  };

  return (
    <div
      className={`absolute ${positionClasses[pos]} font-mono text-sm tracking-widest uppercase`}
      style={{
        color: crtEnabled
          ? `rgba(var(--foreground-rgb), var(--crt-text-opacity))`
          : `rgba(var(--foreground-rgb), 0.4)`,
        opacity: fadeOut ? 0 : visible ? 1 : 0,
        // Combine transition + delay into single property to avoid React warning
        transition: `opacity ${fadeOut ? "600ms" : "150ms"} ease-out ${visible && !fadeOut ? `${index * 100}ms` : "0ms"}`,
        // CRT chromatic aberration (only when CRT enabled)
        textShadow: crtEnabled ? `var(--crt-text-shadow)` : "none",
        // CRT trapezoid perspective - extreme for small text (only when CRT enabled)
        transform: crtEnabled ? "perspective(300px) rotateX(15deg)" : undefined,
        transformOrigin: crtEnabled ? "bottom center" : undefined,
      }}
    >
      {displayText}
    </div>
  );
}

export function StarLoader({ onComplete, assetsReady = false, skipTail = false }: StarLoaderProps) {
  const [state, setState] = useState<LoaderState>("entering");
  const [showRipple, setShowRipple] = useState(false);
  const hasCompletedRef = useRef(false);

  // Animation progress for the contraction (0 = full screen, 1 = star)
  const [progress, setProgress] = useState(0);

  // Phase 1: Animate contraction over 800ms
  useEffect(() => {
    const startTime = performance.now();
    const duration = 800;

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const p = Math.min(1, elapsed / duration);

      // Spring easing with oscillation
      const eased = springEase(p);

      setProgress(eased);

      if (p < 1) {
        requestAnimationFrame(animate);
      } else {
        setState("holding");
      }
    };

    requestAnimationFrame(animate);
  }, []);

  // Track when corner text should be visible (after initial fast contraction)
  const CRT_DISPLAY_DURATION = skipTail ? 0 : 5000; // 0 when no starfield to coordinate with
  const crtStartRef = useRef<number>(0);

  // Phase 2: When assets ready + holding, trigger snap → bloop sequence
  useEffect(() => {
    if (state === "holding") {
      // Mark when CRT text became visible
      if (crtStartRef.current === 0) {
        crtStartRef.current = performance.now();
      }

      if (assetsReady && !hasCompletedRef.current) {
        const elapsed = performance.now() - crtStartRef.current;
        const remaining = Math.max(0, CRT_DISPLAY_DURATION - elapsed);

        // Wait for CRT display duration, then snap
        setTimeout(() => {
          hasCompletedRef.current = true;
          setState("snapping");

          // After 80ms snap, trigger bloop
          setTimeout(() => {
            setState("blooping");
            setShowRipple(true);

            // After bloop, start fading CRT text while starfield disperses
            setTimeout(() => {
              setState("fading");
              onComplete(); // Start starfield here

              // Fully remove after CRT text fade completes (skipped when no starfield)
              setTimeout(() => {
                setState("done");
              }, skipTail ? 0 : 1000);
            }, 200);
          }, 80);
        }, remaining);
      }
    }
  }, [state, assetsReady, onComplete]);

  if (state === "done" || state === "fading") return null;

  const isBlooping = state === "blooping";
  const isSnapping = state === "snapping";

  // White rectangle contracts, corners eaten away first → 4-pointed star
  //
  // KEY: Corners race to center FAST, points stay extended LONGER
  // This creates the optical 4-pointed star (not octagon)
  //
  // 8 vertices: 4 star points (cardinal) + 4 corner indents

  // Corners: aggressive ease - race to center early
  // Use power curve to make corners move much faster than linear
  const cornerEased = Math.pow(progress, 0.3); // Accelerates early, 0.3 = very aggressive
  const cornerDist = (1 - cornerEased) * 50; // 50 -> 0

  // Points: gentle ease - stay extended, contract late
  // Use inverse power curve to keep points out longer
  const pointEased = Math.pow(progress, 2); // Stays slow early, accelerates late
  const pointDist = 50 - pointEased * 48; // 50 -> 2

  // Square container sized to always cover viewport diagonal, then shrinks to star
  // Using vmax * 1.5 ensures full coverage even on wide/tall screens
  // At progress 1: shrinks to small centered star

  const containerSize = 200 - progress * 185; // 200vmax -> 15vmax

  return (
    <div className="fixed inset-0 z-[10000] pointer-events-none overflow-hidden">
      {/* Square container for symmetric star - always centered */}
      <div
        className="absolute"
        style={{
          width: `${containerSize}vmax`,
          height: `${containerSize}vmax`,
          top: "50%",
          left: "50%",
          transform: isBlooping
            ? "translate(-50%, -50%) scale(2)"
            : isSnapping
            ? "translate(-50%, -50%) scale(0.5)"
            : "translate(-50%, -50%)",
          opacity: isBlooping ? 0 : 1,
          transition: isBlooping
            ? "transform 200ms ease-out, opacity 200ms ease-out"
            : isSnapping
            ? "transform 80ms cubic-bezier(0.4, 0, 1, 1)"
            : "none",
        }}
      >
        <div
          className="absolute inset-0 bg-[var(--foreground)]"
          style={{
            clipPath: `polygon(
              50% ${50 - pointDist}%,
              ${50 + cornerDist}% ${50 - cornerDist}%,
              ${50 + pointDist}% 50%,
              ${50 + cornerDist}% ${50 + cornerDist}%,
              50% ${50 + pointDist}%,
              ${50 - cornerDist}% ${50 + cornerDist}%,
              ${50 - pointDist}% 50%,
              ${50 - cornerDist}% ${50 - cornerDist}%
            )`,
          }}
        />
      </div>

      {/* Ripple/shockwave ring */}
      {showRipple && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="rounded-full border-2 border-[var(--foreground)]"
            style={{
              width: 40,
              height: 40,
              animation: "starRipple 400ms ease-out forwards",
            }}
          />
        </div>
      )}

      <style jsx>{`
        @keyframes starRipple {
          0% {
            width: 40px;
            height: 40px;
            opacity: 0.6;
          }
          100% {
            width: 300px;
            height: 300px;
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}
